import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Stethoscope, User, Lock, X } from 'lucide-react'
import { authApi } from '../../lib/api'
import { useAuth } from '../../lib/auth'

const MedicalLogin = () => {
  const navigate = useNavigate()
  const { login: authLogin } = useAuth()
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    document.title = '医护登录 - 眉山疾控积分系统'
  }, [])

  const handleLogin = async () => {
    setError('')
    if (!phone || !password) {
      setError('请输入手机号和密码')
      return
    }
    setLoading(true)
    try {
      const resp = await authApi.login(phone, password)
      // 验证是否有医护权限：检查 medical_role 是否存在
      if (!resp.account.medical_role) {
        setError('此账号没有医护权限，请从用户端登录')
        setLoading(false)
        return
      }
      // 调用 AuthProvider.login() 设置全局认证状态
      authLogin(resp)
      // 兼容旧版存储
      localStorage.setItem('medicalLoggedIn', 'true')
      localStorage.setItem('medicalUser', JSON.stringify({
        id: resp.account.id,
        name: resp.account.name,
        phone: resp.account.phone,
        hospital: resp.account.hospital_name,
        hospital_id: resp.account.hospital_id,
        hospital_name: resp.account.hospital_name,
        role: resp.account.medical_role,
        idCard: resp.account.id_card,
        avatar: resp.account.avatar,
      }))
      /* 机构负责人登录后直接进管理后台：那里有完整的积分运营（事件类型 / 积分录入 /
         积分记录 / 礼品管理 / 兑换记录 / 事件审核）+ 数据看板 + 用户与机构。
         2026-05-13 fca6fff 曾移除这个分支，导致机构负责人被困在医护端少数几项菜单里
         （2026-09-20 恢复）。普通医护仍留在医护端。 */
      if (resp.account.medical_role === 'supervisor') {
        navigate('/admin/dashboard')
      } else {
        navigate('/medical/dashboard')
      }
    } catch (e) {
      setError((e as Error).message || '登录失败，请检查手机号和密码')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-700 to-green-600 flex">
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12">
        <div className="text-center">
          <svg className="w-64 h-64 mx-auto mb-8" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="100" cy="100" r="90" stroke="rgba(255,255,255,0.2)" strokeWidth="4" fill="none" />
            <circle cx="100" cy="100" r="70" stroke="rgba(255,255,255,0.15)" strokeWidth="3" fill="none" />
            <rect x="65" y="40" width="70" height="120" rx="14" fill="rgba(255,255,255,0.92)" />
            <rect x="86" y="60" width="28" height="8" rx="4" fill="#16a34a" />
            <rect x="86" y="74" width="28" height="8" rx="4" fill="#16a34a" opacity="0.7" />
            <rect x="86" y="88" width="20" height="8" rx="4" fill="#16a34a" opacity="0.5" />
            <polyline points="70,130 82,130 88,116 94,144 100,122 106,130 130,130" stroke="#16a34a" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="100" cy="18" r="6" fill="white" />
            <circle cx="170" cy="65" r="4" fill="white" />
            <circle cx="170" cy="135" r="5" fill="white" />
            <circle cx="30" cy="65" r="4" fill="white" />
            <circle cx="30" cy="135" r="5" fill="white" />
          </svg>
          <h2 className="text-3xl font-bold text-white mb-4">机构端管理后台</h2>
          <p className="text-green-200 text-lg">积分录入 · 事件审核 · 高效管理</p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-center mb-8 lg:justify-start">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
              <Stethoscope className="w-7 h-7 text-green-600" />
            </div>
            <span className="ml-3 text-2xl font-bold text-white">机构人员登录</span>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <h3 className="text-xl font-bold text-gray-800 mb-2 text-center lg:text-left">欢迎回来</h3>
            <p className="text-sm text-gray-500 mb-6 text-center lg:text-left">请使用为您分配的账号登录</p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center">
                <X className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
                <span className="text-red-600 text-sm">{error}</span>
              </div>
            )}

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">手机号</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel" placeholder="请输入手机号"
                    value={phone} onChange={e => setPhone(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">密码</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'} placeholder="请输入密码"
                    value={password} onChange={e => setPassword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleLogin()}
                    className="w-full pl-12 pr-12 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button onClick={handleLogin} disabled={loading}
                className={`w-full py-3 rounded-xl font-medium transition-all flex items-center justify-center ${
                  loading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-green-700 text-white hover:bg-green-800'
                }`}>
                {loading ? '登录中...' : '登录'}
              </button>
            </div>
          </div>

          <p className="text-center text-white/50 text-sm mt-6">© 2026 眉山疾控</p>
        </div>
      </div>
    </div>
  )
}

export default MedicalLogin
